import React from "react";
import Modal from "react-responsive-modal";
import * as Icon from "react-feather";

import UserService from "../../services/UserService";
import UtilityService from "../../services/UtilityService";

import TextAreaField from "../common/text_area_field/TextAreaField";
import TextFieldInput from "../common/text_field_input/TextFieldInput";
import RolePicker from "../common/role_picker/RolePicker";
import SearchableDropdown from "../common/searchable_dropdown/SearchableDropdown";
import WComponent from "../common/WComponent";

import Button from "../common/button/Button";
import Loader from "../common/loader/Loader";

import "../common/modal/modal.css";
import "./team-modal.css";
import RequiredLabel from "../common/required_label";

const { alert } = UtilityService;

class TeamModal extends WComponent {
	constructor() {
		super();
		this.state = {
			teamId: "new",

			loading: false,
			show: false,

			name: "",
			description: "",
			teamUsers: []
		};
	}

	componentDidMount() {
		this.resetComponent();
	}

	componentDidUpdate(prevProps) {
		let { show } = this.props;

		if (prevProps.show !== show || prevProps.teamId !== this.props.teamId) {
			this.update({ show });

			if (show) {
				this.resetComponent();
			}
		}
	}

	async resetComponent() {
		let { teamId } = this.props;

		await this.update({
			loading: true,
			teamId,
			name: "",
			description: "",
			teamUsers: []
		});

		if (teamId !== "new") {
			await this.fetchTeam();
		}

		await this.update({
			loading: false
		});
	}

	fetchTeam = async () => {
		let { teamId } = this.props;

		let { id, name, description, users } = await UserService.fetchTeam({ teamId });

		await this.update({
			teamId: id,
			name,
			description,
			teamUsers: users
		});
	};

	show() {
		this.update({
			show: true
		});
	}

	validateRoles({ teamUsers }) {
		const allValidRoles = teamUsers.every(user => user.roleName && user.roleName !== "No Role");
		if (!allValidRoles) {
			if (alert) {
				alert({ type: "info", text: "Please fill in all roles for the users." });
			}
			return false;
		}
		return true;
	}

	updateTeam = async () => {
		const missing = this.getMissingRequiredFields();
		if (missing.length) {
			alert({ type: "error", text: `Please fill: ${missing.join(", ")}` });
			return false;
		}

		let { teamId, name, description, teamUsers } = this.state;

		let { validateRoles } = this;
		if (!validateRoles({ teamUsers })) {
			return false;
		}

		this.update({
			loading: true
		});

		let success = await UserService.updateTeam({ teamId, name, description, users: teamUsers });

		if (!success) {
			alert({ type: "info", text: "Failed to update team." });
		} else {
			alert({ type: "info", text: "Updated team successfully!" });
		}

		this.update({
			loading: false
		});

		return true;
	};

	isValid = () => this.getMissingRequiredFields().length === 0;

	createTeam = async () => {
		let { name, description, teamUsers } = this.state;
		let { validateRoles } = this;

		const missing = this.getMissingRequiredFields();
		if (missing.length) {
			alert({ type: "error", text: `Please fill: ${missing.join(", ")}` });
			return false;
		}

		if (!validateRoles({ teamUsers })) {
			return false;
		}

		this.update({
			loading: true
		});

		let success = await UserService.createTeam({ name, description, users: teamUsers });

		if (!success) {
			alert({ type: "error", text: "Failed to create team." });
		} else {
			alert({ type: "success", text: "Created team successfully!" });
		}

		this.update({
			loading: false
		});

		return true;
	};

	isNewTeam = () => {
		return this.state.teamId === "new";
	};

	onChange = async e => {
		let target = e.target;
		let value = target.type === "checkbox" ? target.checked : target.value;
		await this.update({
			[target.name]: value
		});
	};

	onClose = async confirmed => {
		let shouldClose = true;
		let { isNewTeam, createTeam, updateTeam } = this;
		if (confirmed) {
			if (isNewTeam()) {
				shouldClose = await createTeam();
			} else {
				shouldClose = await updateTeam();
			}
		}

		if (shouldClose) {
			await this.update({
				show: false
			});

			if (this.props.onClose) {
				this.props.onClose();
			}
		}
	};

	onSelectUser = async user => {
		let { teamUsers, teamId } = this.state;

		teamUsers.push({
			user_id: user.id,
			team_id: teamId,
			role_id: 0,

			// render part
			userName: user.name,
			roleName: "Staff"
		});

		await this.update({ teamUsers });
	};

	onRemoveMember = async index => {
		let { teamUsers } = this.state;

		teamUsers.splice(index, 1);
		let newTeamUsers = teamUsers.slice();

		await this.update({
			teamUsers: newTeamUsers
		});
	};

	onSelectRole = async (userIndex, role) => {
		let { teamUsers } = this.state;

		teamUsers = teamUsers.slice();

		teamUsers[userIndex].role_id = role.id;
		teamUsers[userIndex].roleName = role.name;

		await this.update({
			teamUsers
		});
	};

	getMissingRequiredFields = () => {
		const { name } = this.state;
		const missing = [];
		if (!name?.trim()) missing.push("Team Name");
		return missing;
	};

	renderMembers = () => {
		let { teamUsers } = this.state;
		let { onRemoveMember, onSelectUser, onSelectRole } = this;

		return (
			<div className="team__members">
				<SearchableDropdown title="Add a member ..." onSelect={onSelectUser} type={"users"} clearOnSelect showAll className="ft-team-member-field"/>
				<div className="team__members__title">Current Team Members:</div>
				{teamUsers.length > 0 && (
					<div className="team__members__list">
						{teamUsers.map((teamUser, index) => {
							return (
								<div className="team__members__list__item">
									<div className="team__members__list__item__txt">
										{teamUser.userName}
										<RolePicker roleId={teamUser.roleName} onSelect={role => onSelectRole(index, role)} />
									</div>
									<Icon.XCircle
										size={28}
										color="#FF6B6B"
										onClick={() => {
											onRemoveMember(index);
										}}
										className="ft-remove-team-user"
									/>
								</div>
							);
						})}
					</div>
				)}
			</div>
		);
	};

	render() {
		let { show, name, description, loading } = this.state;

		return (
			<Modal open={show} onClose={() => this.onClose(false)} center>
				<div>
					<div className="modal-title">{this.isNewTeam() ? "New Team" : name}</div>

					{loading ? (
						<div className="container-loader">
							<Loader />
						</div>
					) : (
						<div className="team-modal__body">
							<TextFieldInput title={<RequiredLabel text="Team Name" />} name="name" value={name} onChange={this.onChange} autoComplete="off" className="ft-team-name-field"/>
							<TextAreaField title="Description" type="text" name="description" value={description} onChange={this.onChange} className="ft-team-description-field"/>

							{this.renderMembers()}
						</div>
					)}
				</div>

				<div className="modal-footer">
					<Button onClick={() => this.onClose(true)} text={this.isNewTeam() ? "Create Team" : "Update Team"} className="ft-team-createupdate-btn" />

					<Button onClick={() => this.onClose(false)} text="Cancel" className="ft-team-cancel-btn"/>
				</div>
			</Modal>
		);
	}
}

export default TeamModal;
